// src/lib/editorCapabilities.ts
//
// Single source of truth for the 2D video provider capability matrix. Lifted
// from the per-modal definition in Animate2dModal.tsx so the wizard and any
// other future surface can derive the SAME constraints (durations, resolutions,
// costs, tier labels) from one place. Backend remains the enforcement boundary
// — this matrix is for guiding the UI, not for security.

import type { Video2dProviderId } from '@/services/avatarApi';

export type Duration = 5 | 6 | 10;
export type Resolution = '512p' | '768p' | '1080p';

export interface ProviderMeta {
  id: Video2dProviderId;
  label: string;
  tier: 'cheap' | 'balanced' | 'premium';
  blurb: string;
  durations: ReadonlyArray<Duration>;
  resolutions: ReadonlyArray<Resolution>;
  /** cents/sec keyed by resolution (matches backend estimateCostCents). */
  costPerSecCents: Record<Resolution, number>;
  /** Flat base price in cents for fixed-price providers (Kling 5s = 25c). */
  basePriceCents?: number;
  /**
   * Hint shown when the user picks a transparency mode the provider rejects
   * outright — Veo silently auto-upgrades chroma-key → matting at runtime, so
   * we surface that contract here rather than throw a confusing 422.
   */
  chromaKeyAutoUpgrade?: boolean;
}

export const PROVIDERS: ReadonlyArray<ProviderMeta> = [
  {
    id: 'pixverse-fal',
    label: 'PixVerse',
    tier: 'cheap',
    blurb: 'Cheapest. 540/720/1080p, native audio support.',
    durations: [5, 10],
    resolutions: ['512p', '768p', '1080p'],
    costPerSecCents: { '512p': 3.5, '768p': 4.5, '1080p': 9.0 },
  },
  {
    id: 'hailuo-fal',
    label: 'Hailuo',
    tier: 'balanced',
    blurb: 'Body movement specialist. 512/768p, no audio.',
    durations: [6, 10],
    resolutions: ['512p', '768p'],
    costPerSecCents: { '512p': 1.7, '768p': 4.5, '1080p': 0 },
  },
  {
    id: 'vidu-fal',
    label: 'Vidu Q3',
    tier: 'balanced',
    blurb: 'Smart Cuts + native audio. 720/1080p.',
    durations: [5, 10],
    resolutions: ['512p', '768p', '1080p'],
    costPerSecCents: { '512p': 5.0, '768p': 5.0, '1080p': 7.0 },
  },
  {
    id: 'kling-fal',
    label: 'Kling 2.1',
    tier: 'premium',
    blurb: 'Cinematic gold standard. 1080p fixed.',
    durations: [5, 10],
    resolutions: ['1080p'],
    costPerSecCents: { '512p': 0, '768p': 0, '1080p': 5.0 },
    basePriceCents: 25,
  },
  {
    id: 'veo31-fast-fal',
    label: 'Veo 3.1 Fast',
    tier: 'premium',
    blurb: 'Google Veo 3.1 fast tier. Top coherence, 1080p native.',
    durations: [5, 6, 10],
    resolutions: ['512p', '768p', '1080p'],
    costPerSecCents: { '512p': 10, '768p': 10, '1080p': 10 },
    chromaKeyAutoUpgrade: true,
  },
  {
    id: 'veo31-fal',
    label: 'Veo 3.1',
    tier: 'premium',
    blurb: 'Google Veo 3.1 flagship. Best-in-class motion + character fidelity.',
    durations: [5, 6, 10],
    resolutions: ['512p', '768p', '1080p'],
    costPerSecCents: { '512p': 20, '768p': 20, '1080p': 20 },
    chromaKeyAutoUpgrade: true,
  },
];

export const TIER_LABEL: Record<ProviderMeta['tier'], string> = {
  cheap: 'Cheap',
  balanced: 'Balanced',
  premium: 'Premium',
};

export function getProviderMeta(id: Video2dProviderId): ProviderMeta {
  const found = PROVIDERS.find(p => p.id === id);
  if (!found) throw new Error(`Unknown video2d provider: ${id}`);
  return found;
}

/**
 * Snap a user's selected (duration, resolution) onto the closest values the
 * provider actually supports. Used when the user switches provider mid-flow:
 * the current selection might not be valid for the new engine.
 */
export function clampToProvider(
  provider: ProviderMeta,
  durationSec: Duration,
  resolution: Resolution,
): { durationSec: Duration; resolution: Resolution } {
  const safeDuration = provider.durations.includes(durationSec)
    ? durationSec
    : provider.durations[0];
  const safeResolution = provider.resolutions.includes(resolution)
    ? resolution
    : provider.resolutions[0];
  return { durationSec: safeDuration, resolution: safeResolution };
}

/**
 * Live cost estimate in dollars for a given provider/duration/resolution
 * combo. Mirrors the per-modal estimate logic. Variants and matting add-ons
 * are layered in by callers (the matrix is per-clip only).
 */
export function estimateClipCostUsd(
  provider: ProviderMeta,
  durationSec: Duration,
  resolution: Resolution,
): number {
  if (provider.basePriceCents && provider.id === 'kling-fal') {
    const extra = Math.max(0, durationSec - 5);
    return (provider.basePriceCents + extra * 5) / 100;
  }
  const perSec = provider.costPerSecCents[resolution] ?? 0;
  return (durationSec * perSec) / 100;
}

/**
 * Phase 7c — matting follow-up cost (veed/fal). Fixed roughly per-second.
 * Returns 0 for transparency='none' or 'chroma-key' (no backend cost).
 */
export function mattingAddOnUsd(
  durationSec: Duration,
  transparency: 'none' | 'chroma-key' | 'matting',
): number {
  if (transparency !== 'matting') return 0;
  // veed pricing 2026-05: $0.0225 / 30 frames @ 24 fps ≈ $0.018/s
  return durationSec * 0.018;
}

/**
 * 3D mesh provider → compatible animation library mapping. Used by the
 * wizard's 3D side-branch (when we ship it) and by AnimateLibraryModal.
 *
 * tripo meshes can only be rigged + retargeted by Tripo's preset utility
 * library; meshy meshes can use the Dancing curated set OR the Full 556
 * catalog. Rodin meshes have no library compatibility yet.
 */
export type MeshProvider = 'tripo' | 'meshy' | 'rodin-fal' | 'text-source' | 'multi-image-source' | 'mixamo-manual';
export type AnimationLibraryId = 'tripo-utility' | 'meshy-dancing' | 'meshy-full';

export function getCompatibleAnimationLibraries(
  meshProvider: MeshProvider | undefined,
): ReadonlyArray<AnimationLibraryId> {
  if (!meshProvider) return [];
  if (meshProvider === 'tripo') return ['tripo-utility'];
  if (meshProvider === 'meshy' || meshProvider === 'text-source' || meshProvider === 'multi-image-source') {
    return ['meshy-dancing', 'meshy-full'];
  }
  // rodin-fal + mixamo-manual: no library compatibility yet.
  return [];
}
